import * as Sentry from '@sentry/react';

import { gengql } from '@/__generated__';
import {
  AgreementInput,
  AuthCreateTokenMutation,
  SignupSubtype,
  SignupType,
} from '@/__generated__/graphql';
import { notify } from '@/actions/ui';
import { authSession } from '@/auth/utils/authSession';
import { AUTH_TOKEN_CACHE_KEY } from '@/auth/utils/authTokenKey';
import {
  abandonCognitoSignIn,
  clearPendingCognitoSignIn,
  cognitoPasswordLogin,
} from '@/auth/utils/cognitoLogin';
import {
  endCognitoSession,
  isCognitoLoginEnabled,
  peekCognitoIdToken,
} from '@/auth/utils/cognitoSession';
import { CognitoSignInError } from '@/auth/utils/cognitoSignIn';
import { acceptInvitationAfterLogin } from '@/auth/utils/invitationAfterLogin';
import { clearPendingSocialLinks } from '@/auth/utils/pendingSocialLinkHandoff';
import {
  type SocialCredential,
  type SocialProvider,
  completeSocialSignIn,
  connectSocialIdentity,
  socialProviderName,
} from '@/auth/utils/socialSignIn';
import { clearSocialSignupHandoffs } from '@/auth/utils/socialSignupHandoff';
import config from '@/config';
import { gtm } from '@/core/analytics';
import { ERROR_CODES, GraphQLClient, basicAuth, firstErrorCode, hasErrorCode } from '@/core/api';
import { clearInvitationToken, getInvitationToken, settleCachedInvitation } from '@/core/invite';
import { savePostLoginNotice } from '@/core/postLoginNotice';
import { clearTracking } from '@/core/tracking';
import { UserContext } from '@/core/user';
import { Viewer } from '@/core/viewer';
import { GroupDomain } from '@/group';
import { useApp } from '@/hooks/useAppContext';
import store, { AppStore } from '@/store';
import { setRuntimeVariable } from '@/store/runtime';
import { clearCache, setCache } from '@/utils';

export { SignupSubtype, SignupType } from '@/__generated__/graphql';
export const defaultNext = '/dashboard';

function formatPath(path: string, domain?: GroupDomain, signupType?: SignupType) {
  const domainPart = domain ? `/${domain.subdomain}` : '';
  const signupTypePart = signupType ? `/${signupType === 'client' ? 'member' : signupType}` : '';
  return `${domainPart}${path}${signupTypePart}`;
}

function addSearchParam(urlStr: string, field: string, value?: unknown) {
  if (!value) return urlStr;
  const querySign = urlStr.indexOf('?') === -1 ? '?' : '&';
  return `${urlStr}${querySign}${field}=${value}`;
}

interface AuthPathOptions {
  to: string;
  domain?: GroupDomain;
  signupType?: SignupType;
  invite?: string;
  next?: string;
}

export function formatAuthPath({ to, domain, signupType, invite, next }: AuthPathOptions): string {
  let path = to;
  path = formatPath(path, domain, signupType);
  path = addSearchParam(path, 'invite', invite);
  path = addSearchParam(path, 'next', encodeURIComponent(next || ''));
  return path;
}

// At least 8 characters long
// At least 1 uppercase letter, 1 lowercase letter, 1 digit and 1 special char
// List of special chart https://owasp.org/www-community/password-special-characters
const regex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\s"'^`~!#%&,-/:;<=>@_[\]\\$.|?*+(){}])[A-Za-z\d\s"'^`~!#%&,-/:;<=>@_[\]\\$.|?*+(){}]{8,16}/;
export function isValidPassword(password: string): boolean {
  if (!password) return false;
  return regex.test(password);
}

export type TokenMeta = AuthCreateTokenMutation['createToken'];

interface LoginMeta {
  inviteToken?: string;
  [key: string]: unknown;
}

async function createToken(
  client: GraphQLClient,
  userId: string | undefined,
  loginMeta: LoginMeta,
  recaptchaToken?: string
): Promise<TokenMeta> {
  const { inviteToken } = loginMeta || {};
  const data = await client.mutate(
    gengql(/* GraphQL */ `
      mutation authCreateToken(
        $userId: String
        $inviteToken: String
        $loginMeta: Json
        $recaptchaToken: String
      ) {
        createToken(
          user_id: $userId
          invite_token: $inviteToken
          login_meta: $loginMeta
          recaptcha_token: $recaptchaToken
          client_id: "node"
        ) {
          user_id
          invite_token
          login_meta
          token
        }
      }
    `),
    { userId, inviteToken, loginMeta, recaptchaToken }
  );

  return data.createToken;
}

export function logout(): void {
  // ENG-6102: sign out of Cognito too and tell the other tabs. Synchronous
  // part first (getApiToken stops returning the Cognito token at once, which
  // loginAs relies on); callers about to reload await endCognitoSession().
  // A no-op unless the `cognito-auth` flag started a session at boot.
  // ENG-6136: and before the caches are cleared, so the message to the other
  // tabs names the legacy token this session owned. The clears run even if it
  // throws.
  try {
    void endCognitoSession();
  } finally {
    clearCache('session');
    // ENG-5251 (PR #2688 review): route through authSession so runtime.token
    // is cleared in Redux symmetrically with the cache wipe. Previously this
    // bypass left `runtime.token` stale, and the WebSocket token-getter at
    // index.tsx:252 kept seeing the pre-logout value until the next reload.
    authSession.clearToken();
    clearCache('user_context');
    clearSocialSignupHandoffs();
    clearPendingSocialLinks();
    clearPendingCognitoSignIn();
  }
  // Note: rate display cookie intentionally NOT cleared - persists for rate display
}

export async function loginAs(
  client: GraphQLClient,
  store: AppStore,
  userIdOrEmail: string
): Promise<void> {
  const data = await client.query(
    gengql(/* GraphQL */ `
      query getUser($userId: String!) {
        user(id: $userId) {
          id
          username
        }
      }
    `),
    { userId: userIdOrEmail }
  );

  if (!data.user) {
    throw new Error('User not found');
  }

  const userId = data.user.id;
  try {
    const token = await createToken(client, userId, {
      method: 'admin',
      ua: window.navigator.userAgent,
    });
    logout();
    // ENG-6102: finish the Cognito sign-out before installing the impersonation
    // token; LoginAs hard-navigates next and the boot would otherwise find the
    // staff member's Cognito session still in storage. No-op without Cognito.
    await endCognitoSession();

    setCache(AUTH_TOKEN_CACHE_KEY, token);
    // ENG-5251 (PR #2688 review): mirror the cache write into runtime.token
    // so the WebSocket lazy token-getter (index.tsx:252) sees the new auth
    // without waiting for a page reload.
    store.dispatch(setRuntimeVariable('token', token.token));
  } catch {
    await store.dispatch(notify("You don't have permission to perform this action", 'error'));
    throw new Error('Login as user failed');
  }
}

// SignupSubtype distinguishes funnels within a SignupType. Today only
// SelfService is meaningful (ENG-5201): the API uses it to suppress the
// welcome+verify-link email because that flow uses email-OTP for verification.
// Omitting it preserves the legacy welcome-link behavior.
interface CreateUserOptions {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  recaptcha_token?: string;
  landing_url?: string;
  invite?: string;
  tags?: string[];
  signup_type?: SignupType;
  signup_subtype?: SignupSubtype;
  subdomain?: string;
  phone?: string;
  agreements?: AgreementInput[];
}

async function createUser(client: GraphQLClient, options: CreateUserOptions) {
  const res = await client.mutate(
    gengql(/* GraphQL */ `
      mutation createUser(
        $email: String!
        $password: String!
        $first_name: String!
        $last_name: String!
        $recaptcha_token: String
        $landing_url: String
        $invite: String
        $tags: [String!]
        $signup_type: SignupType
        $signup_subtype: SignupSubtype
        $subdomain: String
        $phone: String
        $agreements: [AgreementInput!]
      ) {
        createUser(
          email: $email
          password: $password
          first_name: $first_name
          last_name: $last_name
          recaptcha_token: $recaptcha_token
          landing_url: $landing_url
          invitation_token: $invite
          tags: $tags
          signup_type: $signup_type
          signup_subtype: $signup_subtype
          subdomain: $subdomain
          phone: $phone
          agreements: $agreements
        ) {
          user {
            id
          }
        }
      }
    `),
    options
  );

  return res.createUser;
}

type SocialCreateUserOptions = Omit<CreateUserOptions, 'password'> & {
  social_signup_token: string;
};

// ENG-6165: a Google or Microsoft sign-up, with socialSignIn's signupToken in place of a
// password. A document of its own so the password signup's stays unchanged and
// valid against an api without social_signup_token.
async function createUserWithSocialSignup(client: GraphQLClient, options: SocialCreateUserOptions) {
  const res = await client.mutate(
    gengql(/* GraphQL */ `
      mutation createUserWithSocialSignup(
        $email: String!
        $social_signup_token: String!
        $first_name: String!
        $last_name: String!
        $recaptcha_token: String
        $landing_url: String
        $invite: String
        $tags: [String!]
        $signup_type: SignupType
        $signup_subtype: SignupSubtype
        $subdomain: String
        $phone: String
        $agreements: [AgreementInput!]
      ) {
        createUser(
          email: $email
          social_signup_token: $social_signup_token
          first_name: $first_name
          last_name: $last_name
          recaptcha_token: $recaptcha_token
          landing_url: $landing_url
          invitation_token: $invite
          tags: $tags
          signup_type: $signup_type
          signup_subtype: $signup_subtype
          subdomain: $subdomain
          phone: $phone
          agreements: $agreements
        ) {
          user {
            id
          }
        }
      }
    `),
    options
  );

  return res.createUser;
}

// ENG-5760: createUser succeeded but the token step failed, so the account
// exists without a session. Callers should send the user to sign in; a retry
// can only fail with "email already exists". `message` is safe to show the
// user as-is, and `cause` keeps the createToken failure for Sentry.
export class SignupSessionError extends Error {
  declare readonly cause: unknown;

  constructor(cause: unknown) {
    super(
      "Your account was created, but we couldn't sign you in automatically. Please sign in to continue."
    );
    this.name = 'SignupSessionError';
    // Non-enumerable like a native ES2022 cause (the lib target is ES2020), so
    // console capture and JSON.stringify don't walk the APIError's variables.
    Object.defineProperty(this, 'cause', { value: cause, configurable: true, writable: true });
  }
}

// ENG-6105: the api writes the pool user after its own commit, inside
// createUser (ENG-6096). That write is synchronous, but the very first sign-in
// can still race it and fail with UserNotFoundException or NotAuthorizedException,
// both of which the wrapper reports as auth.invalid_credentials. Retry once
// after a second before treating it as a stranded signup. Lockout and MFA
// answers carry other codes and are never retried.
export const COGNITO_SIGNUP_RETRY_DELAY_MS = 1000;

async function signInNewCognitoUser(email: string, password: string): Promise<void> {
  try {
    await cognitoPasswordLogin({ email, password });
  } catch (err) {
    if (!hasErrorCode(err, ERROR_CODES.AUTH_INVALID_CREDENTIALS)) throw err;
    await new Promise((resolve) => setTimeout(resolve, COGNITO_SIGNUP_RETRY_DELAY_MS));
    await cognitoPasswordLogin({ email, password });
  }
}

/** A social sign-up: socialSignIn's signup token and the ID token it came from. */
export interface SocialSignup {
  provider: SocialProvider;
  signupToken: string;
  idToken: string;
}

// ENG-6165: the same race for a social sign-up. The custom flow reports a user
// not yet in the pool as auth.social_token_invalid, so that is retried once.
async function signInNewSocialUser(
  client: GraphQLClient,
  email: string,
  { provider, idToken }: SocialSignup
): Promise<void> {
  // The pool user's username attribute is the primary email, lower-cased.
  const credential = { provider, username: email.trim().toLowerCase(), idToken };
  try {
    await completeSocialSignIn(client, credential);
  } catch (err) {
    if (
      !(err instanceof CognitoSignInError && err.code === ERROR_CODES.AUTH_SOCIAL_TOKEN_INVALID)
    ) {
      throw err;
    }
    await new Promise((resolve) => setTimeout(resolve, COGNITO_SIGNUP_RETRY_DELAY_MS));
    await completeSocialSignIn(client, credential);
  }
}

// Exactly one of a password and a social sign-up (ENG-6165).
export type SignupOptions = Omit<CreateUserOptions, 'password'> &
  ({ password: string; social?: undefined } | { password?: undefined; social: SocialSignup });

// `next: string` redirects after successful signup (legacy behavior).
// `next: false` suppresses the redirect AND returns the auth token — used
// by ENG-5201's modal-resident self-service flow, which stays open to drive
// the email-OTP step. Callers in modal-mode are responsible for calling
// authSession.authenticate(token) so the in-page Apollo client picks up
// the new auth context (the redirect path doesn't need this because the
// page reload re-initializes Apollo from cache).
export async function signup(
  client: GraphQLClient,
  options: SignupOptions,
  next: string | false = defaultNext
): Promise<string | undefined> {
  // The provider's ID token stays out of the createUser variables.
  const { social, password, ...fields } = options;
  const data = { ...fields };
  data.invite = data.invite || getInvitationToken();
  //data.login = data.login || true;

  const resp = social
    ? await createUserWithSocialSignup(client, { ...data, social_signup_token: social.signupToken })
    : await createUser(client, { ...data, password });

  gtm({
    event: 'signup',
    signupType: data.signup_type || 'unknown',
    userId: resp.user.id,
  });

  // ENG-6169: the account exists, so the login page's hand-off has served.
  if (social) clearSocialSignupHandoffs();

  // ENG-6105: createUser has accepted the invitation. An MFA challenge or a
  // stranded sign-in leaves before the clear below, and the later login would
  // submit the used token again. Clear it now, only if it is still this one.
  if (data.invite) settleCachedInvitation(data.invite);

  // ENG-6103: with `cognito-auth` on, the new user signs in through Cognito
  // and the session lives there; nothing goes in the legacy token cache.
  // ENG-6165: a social sign-up always does (the buttons need Cognito).
  const cognito = !!social || isCognitoLoginEnabled();
  let token: TokenMeta | undefined;
  try {
    if (social) {
      await signInNewSocialUser(client, data.email, social);
    } else if (cognito) {
      await signInNewCognitoUser(data.email, password);
    } else {
      // Built inside the try: the account already exists, so any failure from
      // here on is a SignupSessionError.
      // eslint-disable-next-line no-param-reassign
      client = new GraphQLClient(config.apiUrl, basicAuth(data.email, password));
      token = await createToken(client, undefined, {
        method: 'password',
        signup: true,
        signupType: data.signup_type || undefined,
        landingUrl: data.landing_url,
        ua: window.navigator.userAgent,
      });
    }
  } catch (err) {
    // BasicAuthMiddleware answers an MFA challenge with a 401 carrying one of
    // these codes; its reason ("otp required") is not a stable contract.
    const mfaRequired =
      hasErrorCode(err, ERROR_CODES.AUTH_MFA_REQUIRED) ||
      hasErrorCode(err, ERROR_CODES.AUTH_MFA_SETUP_REQUIRED);
    // ENG-6103: enrolment happens on the login page; don't leave behind a
    // Cognito session the api refuses.
    if (cognito && hasErrorCode(err, ERROR_CODES.AUTH_MFA_SETUP_REQUIRED)) {
      await abandonCognitoSignIn();
    }
    if (!mfaRequired) {
      const sessionErr = new SignupSessionError(err);
      // Reported here so every signup form counts it the same way. This is the
      // stranded-signup loss metric: GTM's `signup` event has already fired.
      Sentry.captureException(sessionErr, {
        level: 'warning',
        tags: { signup_step: 'create_token' },
      });
      throw sessionErr;
    }
    // Legacy redirect path: stash on /dashboard and let the standard MFA
    // flow take over.
    if (next !== false) {
      window.location.assign(next);
      return undefined;
    }
    // Modal-resident callers (ENG-5201 self-service signup) need an
    // actionable code to render a guided "Sign in to finish MFA setup" CTA
    // — returning undefined leaves them with no way to recover since the
    // user account already exists server-side. Both MFA codes are translated
    // to the AUTH_MFA_ENROLLMENT_REQUIRED registry entry those callers key on.
    const mfaErr = new Error((err as Error).message);
    (mfaErr as Error & { code?: string }).code = ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED;
    throw mfaErr;
  }

  clearInvitationToken();
  clearTracking();

  if (cognito) {
    if (next !== false) {
      window.location.assign(next);
      return undefined;
    }
    // authSession.authenticate recognises the Cognito ID token and leaves the
    // legacy cache alone.
    return peekCognitoIdToken();
  }

  setCache(AUTH_TOKEN_CACHE_KEY, token);
  // ENG-5251 (PR #2688 review): see logout() comment — keep runtime.token
  // in lockstep with the cache so the WebSocket token-getter at
  // index.tsx:252 sees the new auth. The redirect path triggers a reload
  // that would also re-populate runtime.token, but modal-resident callers
  // (next === false) stay in-page and depend on this dispatch.
  store.dispatch(setRuntimeVariable('token', token?.token));

  if (next !== false) {
    window.location.assign(next);
    return undefined;
  }
  // bearerAuth + authSession.authenticate expect the JWT string, not the
  // wrapping TokenMeta object stored in cache.
  return token?.token;
}

interface LoginOptions {
  email: string;
  password: string;
  otp?: string;
  recaptchaToken?: string;
  next?: string;
  /**
   * ENG-6165: a Google or Microsoft account to connect once the password
   * sign-in succeeds, and the email of the account socialSignIn offered it for.
   */
  pendingSocialLink?: PendingSocialLink;
}

export interface PendingSocialLink {
  provider: SocialProvider;
  idToken: string;
  email: string;
}

/** The same address, ignoring case and surrounding space. */
export const isSameEmail = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

// ENG-6192: said once a provider account is connected to an existing account,
// after the redirect's reload.
function notifySocialConnected(provider: SocialProvider): void {
  savePostLoginNotice(`${socialProviderName(provider)} connected`, 'success');
}

// ENG-6165: the api's connect links the provider account to whoever is signed
// in, with no email check, so this is the only guard: connect only when the
// password sign-in was for the account the provider's answer named. Anyone else
// (another person's password on a shared device) gets no link. For Microsoft
// (ENG-6169) this is the only way a link is ever made to an existing account.
// The user is already signed in, so a failed connect never fails the login.
// APIError already redacts the token variable; the Sentry tag carries the code.
export async function connectPendingSocial(link: PendingSocialLink, email: string): Promise<void> {
  if (!isSameEmail(link.email, email)) return;
  try {
    // No explicit auth: authLink sends the new Cognito ID token.
    await connectSocialIdentity(new GraphQLClient(config.apiUrl), link.provider, link.idToken);
    notifySocialConnected(link.provider);
  } catch (err) {
    Sentry.captureException(err, {
      level: 'warning',
      tags: {
        login_step: `connect_${link.provider.toLowerCase()}`,
        error_code: firstErrorCode(err) ?? 'none',
      },
    });
  }
}

export default async function login({
  email,
  password,
  otp,
  recaptchaToken,
  next = defaultNext,
  pendingSocialLink,
}: LoginOptions): Promise<void> {
  // ENG-6103: Cognito sign-in, answering in the same UnauthorizedError codes.
  if (isCognitoLoginEnabled()) {
    await cognitoPasswordLogin({ email, password, otp });
    if (pendingSocialLink) await connectPendingSocial(pendingSocialLink, email);
    window.location.assign(await acceptInvitationAfterLogin(next));
    return;
  }

  const inviteToken = getInvitationToken();

  const client = new GraphQLClient(config.apiUrl, basicAuth(email, password), {
    otp,
  });

  const token = await createToken(
    client,
    undefined,
    {
      method: 'password',
      inviteToken,
      ua: window.navigator.userAgent,
    },
    recaptchaToken
  );

  clearInvitationToken();

  setCache(AUTH_TOKEN_CACHE_KEY, token);
  // ENG-5251 (PR #2688 review): see logout() comment — keep runtime.token
  // in lockstep with the cache. The window.location.assign reload below
  // would otherwise be the only thing that re-populates runtime.token, and
  // any callers reading it between this point and the reload would see
  // the stale value.
  store.dispatch(setRuntimeVariable('token', token?.token));

  window.location.assign(next);
}

/**
 * ENG-6165: finish a Google or Microsoft sign-in for the account the api
 * chose, then go on as a password login does (the cached invitation first).
 * ENG-6192: `connected` when the page just connected the provider account to
 * that account (socialConnectVerify), which the user is told.
 */
export async function loginWithSocial(
  client: GraphQLClient,
  {
    next = defaultNext,
    connected = false,
    ...credential
  }: SocialCredential & { next?: string; connected?: boolean }
): Promise<void> {
  await completeSocialSignIn(client, credential);
  if (connected) notifySocialConnected(credential.provider);
  window.location.assign(await acceptInvitationAfterLogin(next));
}

export function useAuth(): { viewer: Viewer; userContext: UserContext } {
  const { viewer, store } = useApp();
  return { viewer, userContext: store.getState().ui.userContext };
}
